/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { ScreenType } from 'client-code/previews';

import { DEFAULT_RETRY_OPTIONS } from '../../../../config';
import { Channel } from '../../../../core/entities';
import { ServicesClientImpl } from '../../../../core/interactors';
import { NetworkManager } from '../../../../core/network/NetworkManager';
import { RetryMechanism } from '../../../../core/retrymechanism/RetryMechanism';
import { RetryOptions } from '../../../../core/retrymechanism/RetryOptions';
import {
    RootState,
    useTivoDispatch,
    useTivoSelector,
} from '../../../../core/tivo/tivo-redux-wrapper';
import { TChannelV2, TOfferPreview } from '../../../../core/types';
import { onSetFilteredGuideSelectedRowPreviewAction } from '../../../../store/guide/filteredguide/filteredguide.actions';
import { Mousetrap } from '../../../common';
import FilteredGuidePreviewChannel from './FilteredGuidePreviewChannel';
import FilteredGuidePreviewProgram from './FilteredGuidePreviewProgram';
import FilteredGuideSimplePreviewProgram from './FilteredGuideSimplePreviewProgram';

import './FilteredGuidePreview.scss';

type FilteredGuidePreviewProps = {
    allPossibleChannels: Array<Channel | TChannelV2>;
};

const DEBOUNCE_VALUE = 300;

const FilteredGuidePreview = ({ allPossibleChannels }: FilteredGuidePreviewProps) => {
    const services = new ServicesClientImpl();
    const shouldHideMetadata = services.shouldHideMetadata();

    const dispatch = useTivoDispatch();
    const { recordingsStatusMap } = useTivoSelector((state: RootState) => state.recordingsStatus);
    const { selectedProgram, horizontalPosition, filteredGuideTurboScroll } = useTivoSelector(
        (state: RootState) => state.filteredGuide,
    );
    const selectedChannel = useMemo(() => {
        if (!selectedProgram) return null;
        return allPossibleChannels.find((c) => c.channelId === selectedProgram.channelId);
    }, [allPossibleChannels, selectedProgram]);

    const [offerPreviewInfo, setOfferPreviewInfo] = useState<{
        preview: TOfferPreview;
        offerId: string;
    } | null>(null);
    const [offerIdWithPreviewError, setOfferIdWithPreviewError] = useState<string | null>(null);
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    const fetchPreview = useCallback(async (selectedOfferIdProp: string) => {
        const fetchOfferPreview = services.fetchOfferPreviewV2.bind(services);
        const preview = RetryMechanism.callServiceWithRetry<TOfferPreview>(
            fetchOfferPreview,
            [selectedOfferIdProp, ScreenType.All],
            DEFAULT_RETRY_OPTIONS as RetryOptions,
        );
        dispatch(onSetFilteredGuideSelectedRowPreviewAction(preview));

        preview
            .then((data) => {
                if (data) {
                    setOfferPreviewInfo({
                        preview: data,
                        offerId: selectedOfferIdProp,
                    });
                    setOfferIdWithPreviewError(null);
                }
            })
            .catch(() => {
                setOfferPreviewInfo(null);
                setOfferIdWithPreviewError(selectedOfferIdProp);
                dispatch(onSetFilteredGuideSelectedRowPreviewAction(null));
            });
    }, []);

    useLayoutEffect(() => {
        setOfferPreviewInfo(null);
        dispatch(onSetFilteredGuideSelectedRowPreviewAction(null));
        setOfferIdWithPreviewError(null);
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            if (selectedProgram?.offerId) {
                fetchPreview(selectedProgram.offerId);
            }
        }, DEBOUNCE_VALUE);

        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [selectedProgram?.offerId, fetchPreview]);

    const renderedBody = useMemo(() => {
        if (
            !selectedChannel ||
            !selectedProgram ||
            filteredGuideTurboScroll ||
            NetworkManager.isNetworkDisconnected()
        ) {
            return null;
        }

        if (horizontalPosition === 0) {
            return (
                <Mousetrap value={''}>
                    <FilteredGuidePreviewChannel selectedChannel={selectedChannel} />
                </Mousetrap>
            );
        }

        if (offerPreviewInfo && offerPreviewInfo.offerId === selectedProgram.offerId) {
            return (
                <Mousetrap value={''}>
                    <FilteredGuidePreviewProgram
                        key={selectedProgram.offerId}
                        selectedChannel={selectedChannel}
                        offerPreviewInfo={offerPreviewInfo.preview}
                        selectedGuideProgram={selectedProgram}
                        hideMetadata={shouldHideMetadata ? !!selectedProgram.isAdult : false}
                        recordingsStatusMap={recordingsStatusMap}
                    />
                </Mousetrap>
            );
        }
        if (offerIdWithPreviewError && offerIdWithPreviewError === selectedProgram.offerId) {
            return (
                <Mousetrap value={''}>
                    <FilteredGuideSimplePreviewProgram
                        key={selectedProgram.offerId}
                        selectedChannel={selectedChannel}
                        selectedGuideProgram={selectedProgram}
                        hideMetadata={shouldHideMetadata ? !!selectedProgram.isAdult : false}
                        recordingsStatusMap={recordingsStatusMap}
                    />
                </Mousetrap>
            );
        }

        return null;
    }, [
        selectedChannel,
        selectedProgram,
        horizontalPosition,
        offerPreviewInfo,
        offerIdWithPreviewError,
        filteredGuideTurboScroll,
        recordingsStatusMap,
        shouldHideMetadata,
    ]);

    return <div className="filtered-guide-preview">{renderedBody}</div>;
};

export default FilteredGuidePreview;
